import React from 'react';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import { ESTIMATED_LABEL, ESTIMATED_TOOLTIP, NOT_REPORTED } from '../../helper/metricBuckets';
import { contactChannelForColumn, contactRunForColumn } from '../../helper/leadContactStatus';
import LeadContactValues from '../../components/Common/LeadContactValues';

// Source-reported metrics; a blank one was never reported (FE #749).
const REPORTED_METRIC_KEYS = new Set(['employees', 'revenue']);

function cellValue(row, key) {
  const value = row?.[key];
  if (value?.generatedCell) return value.value || 'N/A';
  if (value === undefined || value === null || value === '') {
    return REPORTED_METRIC_KEYS.has(key) ? NOT_REPORTED : 'N/A';
  }
  return String(value);
}

function evidenceText(reference) {
  return reference?.snippet || reference?.value || reference?.text || reference?.url;
}

function statusLabel(cell) {
  if (cell?.refreshStatus === 'running') return 'Refresh in progress';
  if (cell?.status === 'retained_previous_value') return 'Retained previous value';
  if (cell?.status === 'unsupported_no_answer') return 'N/A';
  if (cell?.status === 'unsupported_no_content') return 'N/A';
  if (cell?.status === 'failed_refunded') return 'Failed';
  return null;
}

function renderEvidenceReference(reference, index) {
  const text = evidenceText(reference);
  const isUrl = /^https?:\/\//i.test(String(text || ''));
  const sourceUrl = reference?.source_url;
  return (
    <Box key={`evidence-${index}`} sx={{ mt: 0.5 }}>
      {text && (
        <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
          {isUrl ? (
            <Link href={text} target="_blank" rel="noreferrer" underline="hover">
              {text}
            </Link>
          ) : text}
        </Typography>
      )}
      {sourceUrl && sourceUrl !== text && (
        <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-word' }}>
          <Link href={sourceUrl} target="_blank" rel="noreferrer" underline="hover">
            {sourceUrl}
          </Link>
        </Typography>
      )}
    </Box>
  );
}

function renderGeneratedCell(cell) {
  const label = statusLabel(cell);
  const references = Array.isArray(cell.evidenceRefs) ? cell.evidenceRefs : [];
  return (
    <Box>
      <Typography component="span" variant="body2" sx={{ fontSize: 13 }}>
        {cell.value || 'N/A'}
      </Typography>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {label}
        </Typography>
      )}
      {cell.disclosure && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {cell.disclosure}
        </Typography>
      )}
      {references.map(renderEvidenceReference)}
    </Box>
  );
}

function renderCell(row, column) {
  const rawValue = row?.[column.key];
  if (rawValue?.generatedCell) {
    return renderGeneratedCell(rawValue);
  }
  const value = cellValue(row, column.key);
  const channel = contactChannelForColumn(column.key);
  if (channel && row?.leadContacts?.length) {
    return <LeadContactValues value={value} contacts={row.leadContacts} channel={channel} run={contactRunForColumn(column.key)} />;
  }
  if (column.key === 'employees' && row?.employees_estimated && value !== NOT_REPORTED) {
    return (
      <span>
        {value}{' '}
        <Tooltip title={ESTIMATED_TOOLTIP}>
          <Typography component="span" variant="caption" color="text.secondary" aria-label={ESTIMATED_TOOLTIP}>
            {ESTIMATED_LABEL}
          </Typography>
        </Tooltip>
      </span>
    );
  }
  if (column.key === 'domain' && row?.domain_url && value !== 'N/A') {
    return (
      <Link href={row.domain_url} target="_blank" rel="noreferrer" underline="hover">
        {value}
      </Link>
    );
  }
  if (column.key === 'description') {
    return (
      <span
        style={{
          display: '-webkit-box',
          WebkitLineClamp: '2',
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
        }}
      >
        {value}
      </span>
    );
  }
  return value;
}

export default function ChatArtifactTable({ columns = [], rows = [], sampleLabel }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;

  return (
    <Box>
      {sampleLabel && (
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, letterSpacing: 0 }}>
          {sampleLabel}
        </Typography>
      )}
      <Box
        tabIndex={0}
        sx={{
          width: '100%',
          maxWidth: '100%',
          overflowX: 'auto',
          // FE #748: up to 30 preview rows scroll inside the card under a sticky header.
          maxHeight: 440,
          overflowY: 'auto',
          '& thead th': { position: 'sticky', top: 0, zIndex: 1, bgcolor: '#1F2937' },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          component="table"
          aria-label={sampleLabel || 'Sample preview'}
          sx={{
            width: '100%',
            minWidth: columns.reduce((sum, column) => sum + (column.minWidth || 120), 0),
            borderCollapse: 'separate',
            borderSpacing: 0,
            tableLayout: 'fixed',
          }}
        >
          <Box component="thead" sx={{ bgcolor: '#1F2937' }}>
            <Box component="tr">
              {columns.map((column) => (
                <Box
                  component="th"
                  key={column.key}
                  scope="col"
                  sx={{
                    width: column.minWidth || 120,
                    minWidth: column.minWidth || 120,
                    px: 1.5,
                    py: 1.25,
                    color: '#FFFFFF',
                    fontSize: 12,
                    fontWeight: 700,
                    lineHeight: '18px',
                    textAlign: 'left',
                    letterSpacing: 0,
                    borderRight: '1px solid rgba(255,255,255,0.12)',
                    '&:last-of-type': { borderRight: 0 },
                  }}
                >
                  {column.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {rows.map((row, rowIndex) => (
              <Box component="tr" key={row.id || rowIndex}>
                {columns.map((column) => (
                  <Box
                    component="td"
                    key={column.key}
                    sx={{
                      width: column.minWidth || 120,
                      minWidth: column.minWidth || 120,
                      px: 1.5,
                      py: 1.5,
                      borderTop: '1px solid',
                      borderTopColor: 'divider',
                      borderRight: '1px solid',
                      borderRightColor: 'divider',
                      fontSize: 13,
                      lineHeight: '18px',
                      color: 'text.primary',
                      verticalAlign: 'top',
                      overflow: column.key === 'business_name' ? 'visible' : 'hidden',
                      textOverflow: column.key === 'business_name' ? 'clip' : 'ellipsis',
                      overflowWrap: 'anywhere',
                      wordBreak: 'break-word',
                      '&:last-of-type': { borderRight: 0 },
                    }}
                  >
                    {renderCell(row, column)}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
